/**
 * REST API client — Faz 8.1 (ADR-061).
 *
 * Thin `fetch` wrapper that:
 *   1. Prepends the configured base URL (default same-origin via Vite proxy).
 *   2. Injects `Authorization: Bearer <token>` from `localStorage` on
 *      every `/api/*` request when a token is present.
 *   3. Parses the response as JSON, maps non-2xx into a typed
 *      `ReplayServerError` (5-bucket discriminator).
 *   4. Optionally revives envelope bigint fields via `reviveEnvelopeBigints`.
 */

import { reviveEnvelopeBigints } from "./bigint.js";

export interface ReplayServerError {
  readonly kind: "validation" | "auth" | "not-found" | "upstream" | "internal";
  readonly message: string;
  readonly detail?: Record<string, unknown>;
}

const TOKEN_STORAGE_KEY = "replay-ui:token";

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token: string | null): void => {
  try {
    if (token === null || token === "") localStorage.removeItem(TOKEN_STORAGE_KEY);
    else localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore storage errors (private mode etc.)
  }
};

export interface ApiCallOptions {
  readonly method?: "GET" | "POST";
  readonly body?: unknown;
  readonly reviveBigints?: boolean;
}

export class ApiError extends Error {
  readonly status: number;
  readonly server: ReplayServerError;
  constructor(status: number, server: ReplayServerError) {
    super(`${server.kind}: ${server.message}`);
    this.name = "ApiError";
    this.status = status;
    this.server = server;
  }
}

export const apiCall = async <T>(
  path: string,
  options: ApiCallOptions = {},
): Promise<T> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  const token = getToken();
  if (token !== null && token !== "") {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }

  const res = await fetch(path, init);
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text === "" ? null : JSON.parse(text);
  } catch {
    throw new ApiError(res.status, {
      kind: "upstream",
      message: `non-JSON response: ${text.slice(0, 200)}`,
    });
  }

  if (!res.ok) {
    const err: ReplayServerError =
      parsed !== null && typeof parsed === "object" && "kind" in (parsed as object)
        ? (parsed as ReplayServerError)
        : { kind: "internal", message: `http ${res.status}` };
    throw new ApiError(res.status, err);
  }

  if (options.reviveBigints === true) {
    return reviveEnvelopeBigints(parsed) as T;
  }
  return parsed as T;
};
